/**
 * Counting with arms instead of legs (CAM-06, GDS §15's substitute set).
 *
 * GDS §15 says the Compact substitute may be *hands raised*, *large reaches* or *alternating arm
 * raises*, and then says the thing that matters most: **"the learning mechanic remains counting."**
 * So this detector is deliberately the jump detector's twin rather than a new idea — same shape
 * (a clear start, a return, then the count), same two windows from `GameplayTunables.detector`,
 * same "one action → one event" obligation. What changes is which part of the body is watched.
 *
 * **It reports `capability: 'jump'` on purpose.** From the adapter's point of view this *is* the
 * count slot: it fills the place the jump detector would occupy, its events are `PLAYER_JUMPED`,
 * and its losses must be handled identically. Inventing a seventh detector capability would mean
 * teaching the adapter, the uncertainty ledger and the loss switch about a distinction that exists
 * only in the room — and would risk a Compact sitting's tracking loss being handled by a `default:`
 * branch that quietly does nothing.
 *
 * The event is `PLAYER_JUMPED` for the same reason (D-059's binding parity contract): the semantic
 * event union is unchanged, the matcher counts what it always counted, and the mission runtime and
 * the game's content never learn that this child charged the web with their arms.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../../platform/config/tunables';
import type { JumpSubstitute } from '../../../shared/types/camera-mode';
import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { DEFAULT_POSE_CALIBRATION } from '../pose-calibration';
import { shoulderMidpoint } from '../pose-geometry';
import { POSE_LANDMARK, type PoseObservation } from '../pose-types';
import { createTrackingGate } from './tracking-gate';

const SUBSTITUTE_LANDMARKS = [
  POSE_LANDMARK.leftShoulder,
  POSE_LANDMARK.rightShoulder,
  POSE_LANDMARK.leftWrist,
  POSE_LANDMARK.rightWrist,
];

export type CountArm = 'left' | 'right';

type Arm = CountArm;

/**
 * Which arm last counted, in a holder the adapter can keep across a detector **rebuild**.
 *
 * A grown-up's restart-calibration or substitute change builds a new detector, and a new detector
 * remembers nothing — so a parent who chose "alternating arm raises" for a child who finds one arm
 * easier would silently get "hands raised" back for the next repetition. The memo is a fact about
 * the sitting rather than about the detector instance, so it lives beside it (D-068(2)).
 */
export interface CountAlternationMemo {
  lastCountedArm: CountArm | null;
}

export function createCountAlternationMemo(): CountAlternationMemo {
  return { lastCountedArm: null };
}

const ARMS: readonly Arm[] = ['left', 'right'];
const WRIST_OF: Readonly<Record<Arm, number>> = {
  left: POSE_LANDMARK.leftWrist,
  right: POSE_LANDMARK.rightWrist,
};

export interface CountSubstituteDetectorOptions extends DetectorOptions {
  /** Which of GDS §15's three substitutes this sitting is counting with. */
  readonly substitute: JumpSubstitute;
  /**
   * Where the alternation memory lives, when the caller wants it to outlive this detector.
   *
   * Absent is legitimate — a detector built for one test owns its own memo — and nothing else about
   * the detector changes with it.
   */
  readonly alternation?: CountAlternationMemo | undefined;
}

export function createCountSubstituteDetector(
  options: CountSubstituteDetectorOptions,
): PoseDetector {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const calibration = options.calibration ?? DEFAULT_POSE_CALIBRATION;
  const gate = createTrackingGate({ landmarks: SUBSTITUTE_LANDMARKS, tunables });

  // A large reach asks for the reach the child demonstrated; the other two substitutes are ordinary
  // raises, so they use the ordinary raise threshold. Both are calibrated per child (D-063(1)).
  const liftScale =
    options.substitute === 'large-reach'
      ? calibration.substituteReachScale
      : calibration.handsRaiseScale;
  const liftMargin = calibration.bodyScale * liftScale;
  // The return is a fraction of the lift, exactly as the jump's landing is a fraction of take-off:
  // counting on a full return to the hips would lose the count of a tiring five-year-old.
  const returnMargin = liftMargin * calibration.hysteresisFraction;

  /** Which arm is currently up, or null. For the alternating substitute this is per arm. */
  let liftedArm: Arm | null = null;
  /** The arm that produced the last count, so alternation can be required. Survives a reset. */
  const alternation = options.alternation ?? createCountAlternationMemo();
  /**
   * Neutral re-acquisition (D-068(2)): after a reset or a loss, the arms must be seen back down
   * before a new repetition can arm. A repetition that spans a reset is discarded, never re-armed —
   * otherwise lowering an arm that was raised before a round-context switch counts a charge whose
   * lift belonged to the previous round.
   */
  let awaitingNeutral = false;
  let liftedAt = Number.NEGATIVE_INFINITY;
  let lastLiftAt = Number.NEGATIVE_INFINITY;
  let lastCountedAt = Number.NEGATIVE_INFINITY;

  /** How far one wrist clears the shoulder line. Negative while the arm hangs down. */
  const clearance = (pose: PoseObservation, shoulderY: number, arm: Arm): number => {
    const wrist = pose.landmarks[WRIST_OF[arm]];
    // Frame space grows downward: a raised wrist is above the shoulders, so smaller y.
    return wrist === undefined ? Number.NEGATIVE_INFINITY : shoulderY - wrist.y;
  };

  /** The arm doing the most work right now, at or above `margin`, or null. */
  const armAbove = (pose: PoseObservation, shoulderY: number, margin: number): Arm | null => {
    let best: Arm | null = null;
    let bestClearance = margin;
    for (const arm of ARMS) {
      const value = clearance(pose, shoulderY, arm);
      if (value >= bestClearance) {
        best = arm;
        bestClearance = value;
      }
    }
    return best;
  };

  return {
    capability: 'jump',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);
      if (reading.kind === 'blocked') {
        // A repetition nobody watched finish is not a repetition (recon §B.4).
        if (reading.lost) {
          liftedArm = null;
          awaitingNeutral = true;
        }
        return reading.outcome;
      }

      const shoulders = shoulderMidpoint(reading.pose);
      if (shoulders === null) return { kind: 'none' };

      if (awaitingNeutral) {
        if (armAbove(reading.pose, shoulders.y, returnMargin) !== null) return { kind: 'none' };
        awaitingNeutral = false;
        return { kind: 'none' };
      }

      if (liftedArm === null) {
        // The cooldown runs from where the counted repetition *started*, for the jump detector's
        // reason: the movement occupies both ends, and timing the quiet period from the return
        // would refuse the second of two ordinary repetitions.
        const restingAfterCount = reading.at - lastLiftAt < tunables.detector.actionCooldownMs;
        const lifting = armAbove(reading.pose, shoulders.y, liftMargin);
        if (lifting !== null && !restingAfterCount) {
          liftedArm = lifting;
          liftedAt = reading.at;
        }
        return { kind: 'none' };
      }

      // Still up: the repetition is not finished until this arm comes back down.
      if (clearance(reading.pose, shoulders.y, liftedArm) > returnMargin) return { kind: 'none' };

      const arm = liftedArm;
      liftedArm = null;

      if (reading.at - lastCountedAt < tunables.detector.actionDebounceMs) return { kind: 'none' };

      /**
       * Alternation, and why it refuses rather than re-counts.
       *
       * A parent who chose "alternating arm raises" chose it for a child who finds one arm easier
       * than the other, or who is being asked to cross the midline on purpose. Counting the same
       * arm twice would silently turn that choice back into "hands raised". The refusal is quiet:
       * nothing is marked wrong, and the next lift of the other arm counts normally.
       */
      if (options.substitute === 'alternating-arms' && arm === alternation.lastCountedArm) {
        return { kind: 'none' };
      }

      lastCountedAt = reading.at;
      lastLiftAt = liftedAt;
      alternation.lastCountedArm = arm;
      return {
        kind: 'event',
        event: { type: 'PLAYER_JUMPED' },
        confidence: reading.confidence,
      };
    },

    /**
     * The repetition part-way through is discarded; the count already credited is not (S2-3).
     *
     * **Only when an arm is actually up (S3-2)** — see the jump detector's twin for why: a detector
     * whose arms were already down has nothing to discard, and arming the neutral gate anyway
     * eats the first repetition a child makes after Resume.
     */
    discardInProgress(): void {
      if (liftedArm === null) return;
      liftedArm = null;
      awaitingNeutral = true;
    },

    /** A hole in the observation: a whole repetition can happen inside one (S5-1/S5-3/S5-5). */
    reacquireSilently(): void {
      liftedArm = null;
      awaitingNeutral = true;
      liftedAt = Number.NEGATIVE_INFINITY;
      lastLiftAt = Number.NEGATIVE_INFINITY;
      lastCountedAt = Number.NEGATIVE_INFINITY;
    },

    reset(): void {
      gate.reset();
      liftedArm = null;
      awaitingNeutral = true;
      // `alternation.lastCountedArm` is deliberately NOT cleared: a reset is not a repetition, and
      // forgetting it would hand a parent who chose alternating arms the same arm twice.
      liftedAt = Number.NEGATIVE_INFINITY;
      lastLiftAt = Number.NEGATIVE_INFINITY;
      lastCountedAt = Number.NEGATIVE_INFINITY;
    },
  };
}
